// src/components/OffPropertyPanel.js
//
// THE OFF-PROPERTY PANEL - master list 3.1, the second half of ISS-39, and the screen ruling 177 said
// the platform had never had. It sits beside the status ladder on the claim (ClaimDetail): the status
// ladder shows what the ORGANIZATION is doing; this shows where the claim stands OFF THE PROPERTY -
// what the carrier has received, answered, or let lapse.
//
// It READS and WRITES, and it decides NOTHING: src\data\offProperty.js / appealLadder.js decide, this
// draws and writes. Acts are stored on the claim (appealActs) via claimPatch()/arrayUnion, so they
// inherit the claim's own read/write scoping (firestore.rules claims/{id}) with no new rule - readable
// by the parties to the claim, writable only by the officers the claim's update rule permits. Like
// ruling 70's presentment there is NO extra UI permission gate on the write; the rules are the gate,
// and a refusal is surfaced honestly, never swallowed.
//
// Taken under PD-19, withdrawable by number.
import { appealSigner } from '../data/appealSigner';   // master list item 4
import React, { useEffect, useMemo, useState } from 'react';
import DocumentReview from '../components/DocumentReview';   // section 543: spelling, grammar, facts, prose
import { factsFromClaim as claimFacts, claimRecordLine } from '../data/documentReview';   // section 543
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp, arrayUnion, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {
  offPropertyView, buildAct, PANEL_ACT_KINDS, ACTS, ANSWER_OUTCOMES, SUBMISSION_KINDS, AWARD_OUTCOMES,
  answerBoxLabel, ANSWER_BOX, RULING_268_OUTCOME_BOX,   // ruling 268: the one outcome box
} from '../data/offProperty';
import { appealLetter, boardNoticeLetter } from '../data/offPropertyLetters';
import { resolveContactBlocks } from '../data/letterGroup';
import { resolveRuleText } from '../data/ruleResolver';
import { claimCommittee } from '../data/claimPayload';
import { claimPatch } from '../data/claimWrite';
import RulingRef from './RulingRef';

const C = { panel: '#0B1626', card: '#0D1B2A', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', warn: '#FFD27A' };
const LABEL = { fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 };
const today = () => new Date().toISOString().slice(0, 10);

// A recorded act, shown as a date and a party - never characterised. The label is the model's.
function ActRow({ act }) {
  const spec = ACTS[act.kind] || { label: act.kind, side: '' };
  const tone = spec.side === 'carrier' ? C.warn : C.text;
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px dotted ' + C.line }}>
      <span style={{ fontSize: 12, color: C.dim, minWidth: 92, fontFamily: 'monospace' }}>{act.on || '(undated)'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: tone, fontWeight: 600 }}>
          {/* Record 587 (ruling 267 audit): a refused extension reads as one, never as "extended". */}
          {act.kind === 'extended' && act.granted === false
            ? 'Extension requested, not granted, on ' + (act.on || '(undated)') + (act.agreedWith ? ', ' + act.agreedWith : '')
            : spec.label}
          {(act.kind === 'carrier_answered' || act.kind === 'conference_decided') && act.outcome ? ' — ' + answerBoxLabel(act.outcome) : ''}
          {/* Ruling 268: the amount allowed, where one was recorded. A null figure is not shown as $0 - it was not recorded. */}
          {(act.kind === 'carrier_answered' || act.kind === 'conference_decided') && typeof act.amountAllowed === 'number' ? ' — $' + act.amountAllowed.toFixed(2) + ' allowed' : ''}
          {act.kind === 'appealed' && act.toOfficer ? ' — to ' + act.toOfficer : ''}
          {act.kind === 'board_submitted' && act.submissionKind ? ' — ' + act.submissionKind.replace('_', ' ') : ''}
          {act.kind === 'award_issued' && act.awardOutcome ? ' — ' + act.awardOutcome.replace(/_/g, ' ') : ''}
          {act.kind === 'extended' && act.newDue ? ' — new due date ' + act.newDue + (act.agreedWith ? ', agreed with ' + act.agreedWith : '') + (act.how ? ' (' + act.how + ')' : '') : ''}
        </div>
        {act.projected ? <div style={{ fontSize: 10.5, color: C.dim }}>From the presentment record above — not recorded here (§XI).</div> : null}
        {act.kind === 'carrier_answered' && act.reasonGiven ? (
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
            Carrier’s stated grounds: “{act.reasonGiven}”
            <span style={{ color: act.reasonSource === 'confirmed_from_document' ? C.ok : C.dim }}>
              {act.reasonSource === 'confirmed_from_document'
                ? '  · confirmed from the declination on file' + (act.declinationDocName ? ' (' + act.declinationDocName + ')' : '')
                : '  · recorded by the officer, not from a document'}
            </span>
          </div>
        ) : null}
        {act.note ? <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{act.note}</div> : null}
      </div>
    </div>
  );
}

// Ruling 268 (2026-10-02): the amount allowed - only on Granted / Partially granted, optional, blank is "not recorded".
function AmountAllowed({ f, set }) {
  if (!RULING_268_OUTCOME_BOX) return null;
  const m = ANSWER_BOX.find(b => b.key === f.outcome);
  if (!m || !m.money) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={LABEL}>Amount allowed, in dollars (where money is involved; leave blank if no figure was given)</label>
      <input aria-label="Amount allowed" className="form-input" inputMode="decimal" style={{ fontSize: 12, maxWidth: 200 }} value={f.amountAllowed || ''} onChange={e => set('amountAllowed', e.target.value)} />
    </div>
  );
}

export default function OffPropertyPanel({ claim, claimId, user, documents, onActRecorded }) {
  const isOfficer = !!(user && (user.isAdmin || user.role === 'general_chair' || user.role === 'local_chair'));
  const committee = claimCommittee(claim);
  const scopeKey = claim && claim.scopeKey;

  // recording an act
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('');
  const [on, setOn] = useState('');
  const [f, setF] = useState({});                 // per-kind fields
  const [errs, setErrs] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // letterhead ctx, loaded the way LetterGroupPanel loads it (offices/terms/seats)
  const [ctx, setCtx] = useState(null);
  useEffect(() => {
    if (!isOfficer || !committee) return undefined;
    let dead = false;
    (async () => {
      try {
        const ids = [committee].concat(scopeKey ? [scopeKey] : []);
        const [offs, terms, cmte] = await Promise.all([
          Promise.all(ids.map(id => getDoc(doc(db, 'offices', id)))),
          Promise.all(ids.map(id => getDocs(query(collection(db, 'officeTerms'), where('officeId', '==', id))))),
          getDoc(doc(db, 'committees', committee)),
        ]);
        if (dead) return;
        setCtx({
          offices: offs.filter(s => s.exists()).map(s => ({ officeId: s.id, ...s.data() })),
          terms: terms.flatMap(snap => snap.docs.map(d => ({ termId: d.id, ...d.data() }))),
          seats: cmte.exists() && Array.isArray(cmte.data().seats) ? cmte.data().seats : [],
          carrierContacts: [],   // no carrier-contact record exists yet; the letterhead block says so
        });
      } catch (e) { /* the letterhead simply stays unresolved; drafts say NOT SENDABLE */ }
    })();
    return () => { dead = true; };
  }, [isOfficer, committee, scopeKey]);

  const view = useMemo(() => offPropertyView(claim, today()), [claim]);

  // draft letters (officer)
  const [letterKind, setLetterKind] = useState('');   // '', 'appeal', 'board'
  const [toOfficer, setToOfficer] = useState('');
  const [board, setBoard] = useState('');
  const [subKind, setSubKind] = useState('');
  const [copied, setCopied] = useState(false);

  const ruleTexts = useMemo(() => (Array.isArray(claim && claim.matchedRules) ? claim.matchedRules : []).map(r => {
    const t = r && r.rule ? resolveRuleText(claim.applicableAgreement, r.rule, claim.eventLocation || claim.workLocation, claim.incidentDate) : null;
    return { rule: r && r.rule, text: t ? t.text : '', status: t ? t.status : 'unknown' };
  }), [claim]);

  const draft = useMemo(() => {
    if (!letterKind) return null;
    const lctx = { blocks: ctx ? resolveContactBlocks(claim, { ...ctx, asOf: claim.filedAt || claim.createdAt }) : undefined };
    const signer = appealSigner(user);   // master list item 4: the draft carries the signature block of the officer submitting it
    if (letterKind === 'appeal') return appealLetter(claim, { ...lctx, toOfficer, signer }, ruleTexts);
    if (letterKind === 'board') return boardNoticeLetter(claim, { ...lctx, board, submissionKind: subKind, signer }, ruleTexts);
    return null;
  }, [letterKind, ctx, claim, toOfficer, board, subKind, ruleTexts, user]);

  if (!claim || claim.status === 'draft') return null;   // nothing is off the property until it is filed

  const docList = (Array.isArray(documents) ? documents : (claim.documents || [])).map(d => ({
    path: d.storagePath || d.downloadURL || d.name, name: d.name || d.storagePath || 'document',
  }));

  const needs = kind ? (ACTS[kind].needs || []) : [];

  const reset = () => { setOpen(false); setKind(''); setOn(''); setF({}); setErrs([]); };

  const record = async () => {
    const input = { kind, on, note: f.note };
    if (kind === 'carrier_answered') { input.outcome = f.outcome; input.reasonGiven = f.reasonGiven; }
    // Ruling 268: the conference decision takes the same one box (its `outcome` was required by the model and never sent from here).
    if (kind === 'conference_decided') { input.outcome = f.outcome; input.reasonGiven = f.reasonGiven; }
    if (RULING_268_OUTCOME_BOX && (kind === 'carrier_answered' || kind === 'conference_decided')) input.amountAllowed = f.amountAllowed;   // ruling 268
    if (kind === 'appealed') { input.toOfficer = f.toOfficer; input.receivedOn = f.receivedOn; }   // receivedOn: forensic audit 2026-10-16 (optional)
    // Master list item 4 (Bob 2026-10-14): the one who submits signs - the signed-in officer recording the appeal or notice.
    if (kind === 'appealed' || kind === 'board_notice') {
      input.signer = appealSigner(user);
      if (!input.signer) { setErrs(['The appeal is signed by the one who submits it. This account carries no name and office that can sign an appeal.']); return; }
    }
    if (kind === 'board_notice' || kind === 'board_submitted') input.board = f.board;
    if (kind === 'board_submitted') { input.submissionKind = f.submissionKind; input.docketNumber = f.docketNumber; }
    if (kind === 'award_issued') { input.awardOutcome = f.awardOutcome; input.awardRef = f.awardRef; }
    if (kind === 'extended') {   // ruling 267
      input.granted = f.granted !== false;   // Record 587 (ruling 267 audit): Granted / Not granted
      if (input.granted) input.newDue = f.newDue;
      input.agreedWith = f.agreedWith; input.agreedBy = f.agreedBy; input.how = f.how;
    }

    // The declination link is only meaningful on a carrier answer, and only to a document already on
    // the claim - a link to a document that is not there is never stored.
    let declination = null;
    if (kind === 'carrier_answered' && f.declinationPath) {
      const hit = docList.find(d => d.path === f.declinationPath);
      if (hit) declination = { docPath: hit.path, docName: hit.name, reasonSource: 'confirmed_from_document' };
    }

    const r = buildAct(input, declination);
    setErrs(r.errors || []);
    if (!r.ok) return;

    // recorder provenance - who wrote this, distinct from the party that took the act (ruling 70.3 /
    // PD-0: every recorded fact carries where it came from). appealLadder ignores fields it does not read.
    const stored = {
      ...r.record,
      recordedBy: (user && (user.name || user.email)) || 'Unknown',
      recordedByRole: (user && user.role) || 'unknown',
      recordedByUid: (user && user.uid) || '',
      recordedAt: new Date().toISOString(),
    };
    setSaving(true);
    try {
      await updateDoc(doc(db, 'claims', claimId), claimPatch({
        appealActs: arrayUnion(stored),
        updatedAt: serverTimestamp(),
      }, user));
      if (onActRecorded) onActRecorded(stored);
      reset();
    } catch (e) {
      // A rejected write must never look like a recorded act (PD-5). The form stays with its entry.
      setErrs(['NOT recorded: ' + ((e && e.message) || 'the write was rejected')
        + '. Your entry is still here — nothing was saved. If you are not an officer of this claim, '
        + 'or it has moved past the rung you hold, the record is closed to you here.']);
    } finally { setSaving(false); }
  };

  const st = view.timeline.standing;
  const ans = view.timeline.answer;
  const sil = view.timeline.silence;
  const cc = view.carrierClock;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div>
          <div className="card-title">Off the property — the carrier answer &amp; appeal ladder</div>
          <div className="card-subtitle">
            The status ladder shows what the Organization is doing; this shows where the claim stands with the
            carrier. Elevation moves a claim between offices; presentment moves it off the property.
            <RulingRef user={user} n={177} why="The carrier's responses and the appeals are kept as a second ladder of dated acts, beside the claim's status." />
          </div>
        </div>
      </div>

      {/* WHERE IT STANDS */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: C.card, border: '1px solid ' + C.line, borderRadius: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1 }}>Standing</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>{st.label}</span>
      </div>

      {/* PRESENTMENT (read only — owned by ruling 70's control above) */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 4 }}>Presentment</div>
        {view.presented.recorded ? (
          <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.6 }}>
            {view.presented.text}
            {view.recipientGap ? <div style={{ color: C.warn, marginTop: 4 }}>⚠ {view.recipientGap}</div> : null}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: C.dim }}>
            Not recorded as served on the carrier. Record service on the “Served on the carrier” control above; the
            carrier’s answer clock starts from that date. Until then, the clock has not started.
          </div>
        )}
      </div>

      {/* THE CARRIER ANSWER CLOCK — the highest-value protection, stated honestly */}
      <div style={{ background: C.card, border: '1px solid ' + (sil.lapsed ? C.bad : C.line), borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 4 }}>The carrier’s time to answer</div>
        {ans.state === 'not_presented' && <div style={{ fontSize: 12, color: C.dim }}>{ans.reason}</div>}
        {ans.state === 'answered' && (
          <div style={{ fontSize: 12, color: C.ok }}>The carrier answered on {ans.on} — {answerBoxLabel(ans.outcome)}. The clock is not running.</div>
        )}
        {(ans.state === 'running' || ans.state === 'lapsed') && (
          <div style={{ fontSize: 12, color: sil.lapsed ? C.bad : C.text, lineHeight: 1.6 }}>
            {/* Ruling 266: the receipt line - received, due, and (when it comes) answered. */}
            Received by the carrier <b>{ans.since}</b>. Answer due <b>{ans.due}</b>
            {ans.extendedOn
              ? <> — extended by agreement on {ans.extendedOn}{ans.extendedWith ? ' with ' + ans.extendedWith : ''}.</>
              : <> — {ans.days} days under the agreement’s own time limit.</>}
            {/* Forensic audit 2026-10-16: CSX counts the appeal answer from receipt; say so when the receipt is not recorded. */}
            {ans.receiptNotRecorded && ans.note ? <div style={{ color: C.warn }}>{ans.note}</div> : null}
          </div>
        )}
        {ans.state === 'unknown' && (
          <div style={{ fontSize: 12, color: C.warn, lineHeight: 1.6 }}>
            <b>This platform does not compute a due date, on purpose.</b> {ans.reason}
          </div>
        )}
        {sil.lapsed && (
          <div style={{ fontSize: 12, color: C.bad, marginTop: 6, lineHeight: 1.6 }}>{sil.reason}</div>
        )}

        {/* THE AGREEMENT'S OWN CARRIER CLOCK, VERBATIM (PD-16) — the provision that carries the number,
            shown instead of a computed date. */}
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid ' + C.line }}>
          <div style={{ fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
            What the agreement says the carrier owes
          </div>
          {cc.state === 'not_recorded' && (
            <div style={{ fontSize: 11.5, color: C.dim }}>
              This platform holds no filing requirements for {cc.agreement || 'this agreement'}. That does not mean there
              is none — it means the platform does not hold it. The time limit is in the controlling agreement, and the
              General Chairperson holds authority over it.
            </div>
          )}
          {cc.state === 'catalogued_no_carrier_clock' && (
            <div style={{ fontSize: 11.5, color: C.dim }}>
              {cc.claimsRule
                ? cc.claimsRule + ' is catalogued, but its carrier-answer clock has not been read at source on this platform. It is absent rather than guessed (PD-4); the General Chairperson holds it.'
                : 'No carrier-answer clock is recorded for this agreement.'}
            </div>
          )}
          {cc.state === 'recorded' && cc.clocks.map((k, i) => (
            <div key={i} style={{ fontSize: 11.5, color: C.text, marginBottom: 6 }}>
              <b>{k.label}{k.days ? ' — ' + k.days + ' days' : ''}{k.from ? ' from ' + k.from : ''}.</b>
              {k.onSilence ? <span style={{ color: C.warn }}> On silence: “{k.onSilence}”.</span> : null}
              {k.cite ? <div style={{ fontSize: 10, color: C.dim, fontFamily: 'monospace' }}>{k.cite}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* THE ACTS */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 4 }}>Record of acts</div>
        {view.timeline.acts.length === 0 ? (
          <div style={{ fontSize: 12, color: C.dim }}>No acts recorded off the property yet.</div>
        ) : view.timeline.acts.map((a, i) => <ActRow key={i} act={a} />)}
        {view.timeline.gaps.map((g, i) => (
          <div key={'g' + i} style={{ fontSize: 11, color: C.warn, marginTop: 6 }}>⚠ {g}</div>
        ))}
      </div>

      {/* RECORD AN ACT (officer) — no UI permission gate; the rules are the gate (ruling 70 pattern) */}
      {isOfficer && (
        !open ? (
          <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => { setOpen(true); setErrs([]); }}>+ Record an act</button>
        ) : (
          <div style={{ background: C.card, border: '1px solid ' + C.line, borderRadius: 6, padding: '12px 14px' }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ flex: '1 1 240px' }}>
                <label style={LABEL}>Which act</label>
                <select aria-label="Which act" className="form-select" style={{ fontSize: 12 }} value={kind} onChange={e => { setKind(e.target.value); setF({}); setErrs([]); }}>
                  <option value="">Choose…</option>
                  {PANEL_ACT_KINDS.map(k => <option key={k} value={k}>{ACTS[k].label}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label style={LABEL}>Date it happened</label>
                <input aria-label="Date it happened" className="form-input" type="date" style={{ fontSize: 12 }} value={on} onChange={e => setOn(e.target.value)} />
              </div>
            </div>

            {kind === 'carrier_answered' && (
              <div style={{ marginBottom: 8 }}>
                <label style={LABEL}>The carrier’s answer</label>
                <select aria-label="The carrier’s answer" className="form-select" style={{ fontSize: 12, marginBottom: 8 }} value={f.outcome || ''} onChange={e => set('outcome', e.target.value)}>
                  <option value="">Choose…</option>
                  {ANSWER_OUTCOMES.map(o => <option key={o} value={o}>{answerBoxLabel(o)}</option>)}
                </select>
                <AmountAllowed f={f} set={set} />
                <label style={LABEL}>The carrier’s stated grounds — its own words (a proposal you confirm; never a machine verdict, never sent to any outside AI)</label>
                <textarea aria-label="Reason given" className="form-input" rows={3} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }}
                  value={f.reasonGiven || ''} onChange={e => set('reasonGiven', e.target.value)}
                  placeholder="What the carrier said, as it said it. Read local-only; leave blank if the answer stated no grounds." />
                {docList.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <label style={LABEL}>Link the declination document these grounds came from (optional)</label>
                    <select aria-label="Link the declination document these grounds came from (optional)" className="form-select" style={{ fontSize: 12 }} value={f.declinationPath || ''} onChange={e => set('declinationPath', e.target.value)}>
                      <option value="">None — grounds recorded by the officer</option>
                      {docList.map((d, i) => <option key={i} value={d.path}>{d.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
            {/* Ruling 268: the decision after conference is a step's answer too - the same one box. */}
            {kind === 'conference_decided' && (
              <div style={{ marginBottom: 8 }}>
                <label style={LABEL}>The carrier’s decision</label>
                <select aria-label="The carrier’s decision" className="form-select" style={{ fontSize: 12, marginBottom: 8 }} value={f.outcome || ''} onChange={e => set('outcome', e.target.value)}>
                  <option value="">Choose…</option>
                  {ANSWER_OUTCOMES.map(o => <option key={o} value={o}>{answerBoxLabel(o)}</option>)}
                </select>
                <AmountAllowed f={f} set={set} />
                <label style={LABEL}>The carrier’s stated grounds — its own words</label>
                <textarea aria-label="The carrier’s stated grounds — its own words" className="form-input" rows={3} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }}
                  value={f.reasonGiven || ''} onChange={e => set('reasonGiven', e.target.value)} />
              </div>
            )}
            {kind === 'extended' && (
              <div style={{ marginBottom: 8 }}>
                {/* Record 587 (ruling 267 audit): the extension is a yes/no fact - Granted / Not granted. */}
                <label style={LABEL}>Was the extension granted?</label>
                <select aria-label="Was the extension granted?" className="form-select" style={{ fontSize: 12, marginBottom: 8 }} value={f.granted === false ? 'no' : 'yes'} onChange={e => set('granted', e.target.value !== 'no')}>
                  <option value="yes">Granted</option><option value="no">Not granted</option>
                </select>
                {f.granted !== false && (<>
                  <label style={LABEL}>New due date</label>
                  <input aria-label="New due date" className="form-input" type="date" style={{ fontSize: 12, marginBottom: 8 }} value={f.newDue || ''} onChange={e => set('newDue', e.target.value)} />
                </>)}
                <label style={LABEL}>{f.granted === false ? 'Carrier officer who refused' : 'Carrier officer who agreed'}</label>
                <input aria-label="name and title" className="form-input" style={{ fontSize: 12, marginBottom: 8 }} value={f.agreedWith || ''} onChange={e => set('agreedWith', e.target.value)} placeholder="name and title" />
                <label style={LABEL}>Organization officer who agreed (optional)</label>
                <input aria-label="Organization officer who agreed (optional)" className="form-input" style={{ fontSize: 12, marginBottom: 8 }} value={f.agreedBy || ''} onChange={e => set('agreedBy', e.target.value)} />
                <label style={LABEL}>How it was agreed</label>
                <select aria-label="How it was agreed" className="form-select" style={{ fontSize: 12 }} value={f.how || ''} onChange={e => set('how', e.target.value)}>
                  <option value="">—</option><option value="letter">Letter</option><option value="email">Email</option><option value="conference">Conference</option><option value="telephone">Telephone</option>
                </select>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>An extension not recorded here is not shown as one. Attach the written confirmation to the claim if there is one.</div>
              </div>
            )}
            {needs.includes('toOfficer') && (
              <div style={{ marginBottom: 8 }}>
                <label style={LABEL}>Appealed to which officer</label>
                <input aria-label="Appealed to which officer" className="form-input" style={{ fontSize: 12 }} value={f.toOfficer || ''} onChange={e => set('toOfficer', e.target.value)} placeholder="e.g. Highest Designated Officer" />
                {/* Forensic audit 2026-10-16: where the agreement counts the carrier's answer from RECEIPT (CSX Article 20(d)), this date starts it. */}
                <label style={LABEL}>Received by the carrier on (optional - from the receipt or delivery proof)</label>
                <input aria-label="Received by the carrier on (optional - from the receipt or delivery proof)" className="form-input" type="date" style={{ fontSize: 12 }} value={f.receivedOn || ''} onChange={e => set('receivedOn', e.target.value)} />
              </div>
            )}
            {(kind === 'board_notice' || kind === 'board_submitted') && (
              <div style={{ marginBottom: 8 }}>
                <label style={LABEL}>The Board</label>
                <input aria-label="The Board" className="form-input" style={{ fontSize: 12 }} value={f.board || ''} onChange={e => set('board', e.target.value)} placeholder="e.g. NRAB Fourth Division, PLB 7303" />
              </div>
            )}
            {kind === 'board_submitted' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={LABEL}>Submission</label>
                  <select aria-label="Submission" className="form-select" style={{ fontSize: 12 }} value={f.submissionKind || ''} onChange={e => set('submissionKind', e.target.value)}>
                    <option value="">Choose…</option>
                    {SUBMISSION_KINDS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={LABEL}>Docket number (optional)</label>
                  <input aria-label="Docket number (optional)" className="form-input" style={{ fontSize: 12 }} value={f.docketNumber || ''} onChange={e => set('docketNumber', e.target.value)} />
                </div>
              </div>
            )}
            {kind === 'award_issued' && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={LABEL}>Award outcome</label>
                  <select aria-label="Award outcome" className="form-select" style={{ fontSize: 12 }} value={f.awardOutcome || ''} onChange={e => set('awardOutcome', e.target.value)}>
                    <option value="">Choose…</option>
                    {AWARD_OUTCOMES.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={LABEL}>Award reference (optional)</label>
                  <input aria-label="Award reference (optional)" className="form-input" style={{ fontSize: 12 }} value={f.awardRef || ''} onChange={e => set('awardRef', e.target.value)} />
                </div>
              </div>
            )}
            {kind && (
              <div style={{ marginBottom: 8 }}>
                <label style={LABEL}>Note (optional)</label>
                <input aria-label="Note (optional)" className="form-input" style={{ fontSize: 12 }} value={f.note || ''} onChange={e => set('note', e.target.value)} />
              </div>
            )}

            {errs.length > 0 && (
              <div style={{ border: '1px solid ' + C.bad, background: '#2A0F0F', color: '#F4A0A0', padding: '8px 12px', marginBottom: 8, fontSize: 12, lineHeight: 1.6 }}>
                {errs.map((x, i) => <div key={i}>{x}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" style={{ fontSize: 11 }} disabled={saving} onClick={reset}>Cancel</button>
              <button className="btn btn-secondary" style={{ fontSize: 11, color: C.ok, borderColor: C.ok }} disabled={saving || !kind} onClick={record}>{saving ? 'Recording…' : 'Record it'}</button>
            </div>
          </div>
        )
      )}

      {/* DRAFT LETTERS (officer) — prepared by the platform, adopted and SENT by the officer */}
      {isOfficer && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid ' + C.line }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 6 }}>
            Draft a letter <span style={{ color: C.dim, fontWeight: 400 }}>— a draft you review, adopt, and send yourself. The platform states only the record and sends nothing.</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: letterKind === 'appeal' ? C.accent : C.line, color: letterKind === 'appeal' ? C.accent : C.dim }} onClick={() => { setLetterKind(letterKind === 'appeal' ? '' : 'appeal'); setCopied(false); }}>Appeal to the next officer</button>
            <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: letterKind === 'board' ? C.accent : C.line, color: letterKind === 'board' ? C.accent : C.dim }} onClick={() => { setLetterKind(letterKind === 'board' ? '' : 'board'); setCopied(false); }}>Notice of intent to the Board</button>
          </div>
          {letterKind === 'appeal' && (
            <div style={{ marginBottom: 8 }}>
              <label style={LABEL}>Appeal directed to</label>
              <input aria-label="Appeal directed to" className="form-input" style={{ fontSize: 12, maxWidth: 360 }} value={toOfficer} onChange={e => setToOfficer(e.target.value)} placeholder="the next officer, e.g. Highest Designated Officer" />
            </div>
          )}
          {letterKind === 'board' && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ flex: '1 1 220px' }}><label style={LABEL}>The Board</label><input aria-label="The Board" className="form-input" style={{ fontSize: 12 }} value={board} onChange={e => setBoard(e.target.value)} placeholder="e.g. NRAB Fourth Division" /></div>
              <div style={{ flex: '1 1 160px' }}><label style={LABEL}>Submission</label><select aria-label="Submission" className="form-select" style={{ fontSize: 12 }} value={subKind} onChange={e => setSubKind(e.target.value)}><option value="">Choose…</option>{SUBMISSION_KINDS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select></div>
            </div>
          )}
          {draft && (
            <div>
              <div style={{ fontSize: 12, padding: '8px 12px', border: '1px solid ' + (draft.sendable ? C.ok : C.accent), color: draft.sendable ? C.ok : C.accent, marginBottom: 8 }}>
                {draft.sendable ? 'Every block and field resolved. Review it, then send it yourself.' : (
                  <>NOT READY TO SEND. {draft.unresolvedBlocks.length ? 'Unresolved header block(s): ' + draft.unresolvedBlocks.join(', ') + '. ' : ''}{draft.missingFields.length ? 'Missing: ' + draft.missingFields.join('; ') + '. ' : ''}A draft with [NOT ON FILE] in it is completed before it goes out.</>
                )}
              </div>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: C.text, background: C.panel, border: '1px solid ' + C.line, padding: 12, fontFamily: 'inherit' }}>{draft.text}</pre>
              <DocumentReview title="this letter" kind="appeal" text={draft.text} facts={claimFacts(claim)} record={claimRecordLine(claim)} today={new Date().toISOString().slice(0, 10)} />
              <button className="btn btn-secondary" style={{ fontSize: 11, marginTop: 6 }} onClick={async () => { try { await navigator.clipboard.writeText(draft.text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* clipboard unavailable */ } }}>{copied ? 'Copied' : 'Copy draft'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
